import { KeyboardEventHandler, PropsWithChildren, ReactNode, useState } from 'react';

import { styled } from 'styled-components';

const ARROW_HEIGHT = 7;

const TipContainer = styled.div<{ $hidden: boolean }>(
  ({ $hidden, theme }) => `
  visibility: ${$hidden ? 'hidden' : 'visible'};
  position: absolute;
  bottom: calc(100% + ${ARROW_HEIGHT}px);
  left: 10px;
  background-color: ${theme.colors.grayMedium};
  color: ${theme.colors.grayLightest};
  padding: 14px;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1px solid ${theme.colors.grayMedium};

  /* Arrow */
  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 10px;
    width: 0;
    height: 0;
    border-left: ${ARROW_HEIGHT}px solid transparent;
    border-right: ${ARROW_HEIGHT}px solid transparent;
    border-top: ${ARROW_HEIGHT}px solid ${theme.colors.grayMedium};
  }
`,
);

const Container = styled.span(
  ({ theme }) => `
    position: relative;
    flex: 1;
    height: fit-content;

    &:focus {
      outline: 2px solid ${theme.colors.helsinkiBlue};
      outline-offset: 1px;
    }
`,
);

interface TooltipProps extends PropsWithChildren {
  id: string;
  tipContent: ReactNode;
}

// Assuming that we always need a extra wrapper for this component (unlike for example Material UI).
// Perhaps needs a rethink, if we want to trigger Tooltip specifically from focus on children
export const Tooltip = ({ children, id, tipContent }: TooltipProps) => {
  const [hidden, setHidden] = useState(true);
  const hide = () => {
    setHidden(true);
  };
  const unhide = () => {
    setHidden(false);
  };
  const onEscape: KeyboardEventHandler = (e) => {
    if (e.key === 'Escape') {
      hide();
    }
  };

  return (
    // eslint-disable-next-line styled-components-a11y/no-static-element-interactions
    <Container
      onBlur={hide}
      onFocus={unhide}
      onKeyDown={onEscape}
      onMouseEnter={unhide}
      onMouseLeave={hide}
      // eslint-disable-next-line styled-components-a11y/no-noninteractive-tabindex
      tabIndex={0}
    >
      {children}
      <TipContainer $hidden={hidden} aria-hidden={hidden} id={id} role="tooltip">
        {tipContent}
      </TipContainer>
    </Container>
  );
};
